import React, { useState, useEffect } from 'react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MultiInputTimeRangeField } from '@mui/x-date-pickers-pro';
import BaseModal from '../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput, Checkbox, FormControlLabel, useMediaQuery, Box } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';

dayjs.extend(isSameOrBefore);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const EditEventModal = ({ show, onHide, event, onEdit }) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { organizationCustomers, getFilteredEmployees } = useOrganizationContext();
    const { showToast } = useToast();
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [start, setStart] = useState(dayjs());
    const [timeRange, setTimeRange] = useState([dayjs(), dayjs().add(1, 'hour')]);
    const [isAllDay, setIsAllDay] = useState(false);
    const [location, setLocation] = useState("");
    const [titleError, setTitleError] = useState(false);
    const [timeError, setTimeError] = useState(false);
    const activeEmployees = getFilteredEmployees('active');
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        if (event) {
            setTitle(event.title);
            setDescription(event.extendedProps?.description || '');
            setSelectedEmployees(event.extendedProps?.assigned_employees || []);
            setSelectedCustomer(event.extendedProps?.client_id || '');
            setLocation(event.extendedProps?.location || '');
            
            const startTime = dayjs(event.start);
            setStart(startTime);
            
            if (event.extendedProps?.all_day) {
                setIsAllDay(true);
                setTimeRange([dayjs(startTime).startOf('day'), dayjs(event.end).endOf('day')]);
            } else {
                setIsAllDay(false);
                setTimeRange([dayjs(event.start), dayjs(event.end)]);
            }
        }
    }, [event]);

    const handleSubmit = async () => {
        try {
            setTitleError(false);
            setTimeError(false);
            let valid = true;

            if (!title.trim()) {
                setTitleError(true);
                valid = false;
            }

            if (!isAllDay && (!timeRange[0] || !timeRange[1])) {
                setTimeError(true);
                valid = false;
            }

            if (!isAllDay && timeRange[1].isSameOrBefore(timeRange[0])) {
                setTimeError(true);
                showToast(t(StringKeys.END_TIME_CANNOT_BE_BEFORE_START_TIME), ToastType.DANGER);
                valid = false;
            }

            if (!valid) return;

            setLoadingData(true);

            const eventData = {
                title,
                description,
                assigned_employees: selectedEmployees,
                start: isAllDay ? timeRange[0].startOf('day').toDate() : timeRange[0].toDate(),
                end: isAllDay ? timeRange[1].endOf('day').toDate() : timeRange[1].toDate(),
                client_id: selectedCustomer || null,
                all_day: isAllDay,
                location,
                is_private: event.extendedProps?.is_private || false
            };

            const result = await onEdit(eventData, event);
            
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_UPDATED_EVENT), ToastType.SUCCESS);
                onHide();
            } else {
                showToast(result.error || t(StringKeys.FAILED_TO_UPDATE_EVENT), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error updating event:', error);
            showToast(t(StringKeys.FAILED_TO_UPDATE_EVENT), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    return (
        <BaseModal
            open={show}
            onClose={onHide}
            title={t(StringKeys.EDIT_EVENT)}
            actions={
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button onClick={onHide} color="inherit">
                        {t(StringKeys.CANCEL)}
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {t(StringKeys.SAVE)}
                    </Button>
                </Box>
            }
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            autoFocus
                            label={t(StringKeys.TITLE)}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            error={titleError}
                            helperText={titleError ? t(StringKeys.TITLE_REQUIRED) : ''}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={t(StringKeys.DESCRIPTION)}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={t(StringKeys.LOCATION)}
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DatePicker
                            label={t(StringKeys.DATE)}
                            value={start}
                            onChange={(newValue) => {
                                setStart(newValue);
                                // Update time range to maintain the same time on the new date
                                if (timeRange[0] && timeRange[1]) {
                                    setTimeRange([
                                        newValue.hour(timeRange[0].hour()).minute(timeRange[0].minute()),
                                        newValue.hour(timeRange[1].hour()).minute(timeRange[1].minute())
                                    ]);
                                }
                            }}
                            sx={{ width: "100%" }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAllDay}
                                    onChange={(e) => {
                                        setIsAllDay(e.target.checked);
                                        if (e.target.checked) {
                                            setTimeRange([dayjs(start).startOf('day'), dayjs(start).endOf('day')]);
                                        }
                                    }}
                                />
                            }
                            label={t(StringKeys.ALL_DAY)}
                        />
                    </Grid>

                    {!isAllDay && (
                        <Grid item xs={12}>
                            <MultiInputTimeRangeField
                                value={timeRange}
                                onChange={(newValue) => setTimeRange(newValue)}
                                slotProps={{
                                    textField: ({ position }) => ({
                                        label: position === 'start' ? t(StringKeys.START_TIME) : t(StringKeys.END_TIME),
                                        error: timeError,
                                        helperText: timeError ? t(StringKeys.TIME_REQUIRED) : '',
                                    }),
                                }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>{t(StringKeys.ASSIGN_EMPLOYEES)}</InputLabel>
                            <Select
                                multiple
                                value={selectedEmployees}
                                onChange={(e) => setSelectedEmployees(e.target.value)}
                                input={<OutlinedInput label={t(StringKeys.ASSIGN_EMPLOYEES)} />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const employee = activeEmployees.find((e) => e.id === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={`${employee?.first_name || ''} ${employee?.last_name || ''}`}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                                MenuProps={MenuProps}
                            >
                                {activeEmployees.map((employee) => (
                                    <MenuItem key={employee.id} value={employee.id}>
                                        {employee.first_name} {employee.last_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>{t(StringKeys.ASSIGN_CUSTOMER)}</InputLabel>
                            <Select
                                value={selectedCustomer}
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                input={<OutlinedInput label={t(StringKeys.ASSIGN_CUSTOMER)} />}
                                MenuProps={MenuProps}
                            >
                                <MenuItem value="">
                                    <em>{t(StringKeys.NONE)}</em>
                                </MenuItem>
                                {organizationCustomers.map((customer) => (
                                    <MenuItem key={customer.id} value={customer.id}>
                                        {customer.first_name} {customer.last_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </BaseModal>
    );
};

export default EditEventModal;
